import { Box, LinearProgress, Stack, Typography } from '@mui/material';
import { stk } from '../../styles/stocktakeUi';
import { IStocktakeLocation } from '../../types/api/response/stocktakeresponse';

// Daily / weekly bin-count targets, shown on the Count-a-bin picker and the Approvals board.
// "Counted" = a count POSTED through the app (pending approvals don't count until approved,
// and Cin7-web-UI stocktakes never appear). The weekly figure is UNIQUE bins — re-counting a
// bin later in the week doesn't add again. Days roll over at midnight Fiji. Edit to retune.
export const COUNT_TARGETS = { daily: 30, weekly: 175 };

export interface ICountProgress {
  today: number;
  week: number;
}

// Derived entirely from the /locations payload the picker already loads: a bin was counted
// today when days_since === 0, and within this week (Mon-start) when its last posted count
// is no older than Monday. days_since comes from the server's Fiji clock.
export const computeCountProgress = (locations: IStocktakeLocation[]): ICountProgress => {
  const daysSinceMonday = (new Date().getDay() + 6) % 7;
  const bins = locations.flatMap((l) => l.bins);
  return {
    today: bins.filter((b) => b.days_since === 0).length,
    week: bins.filter((b) => b.days_since != null && b.days_since <= daysSinceMonday).length,
  };
};

const Row = ({ label, value, target }: { label: string; value: number; target: number }) => {
  const met = value >= target;
  return (
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Typography
        variant="caption"
        sx={{ color: stk.inkSoft, fontWeight: 600, width: 64, flex: 'none' }}
      >
        {label}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, (value / target) * 100)}
        color={met ? 'success' : 'primary'}
        sx={{ flex: 1, height: 6, borderRadius: 3 }}
      />
      <Typography
        variant="caption"
        sx={{
          color: met ? stk.green : stk.ink,
          fontWeight: 700,
          width: 56,
          textAlign: 'right',
          flex: 'none',
        }}
      >
        {value} / {target}
      </Typography>
    </Stack>
  );
};

/** Daily/weekly target progress — two bars on the pick screen, one caption line when compact. */
export const CountTargetsPanel = ({
  progress,
  compact = false,
}: {
  progress: ICountProgress;
  compact?: boolean;
}) =>
  compact ? (
    <Typography variant="caption" sx={{ color: stk.inkSoft, fontWeight: 600 }}>
      Bins counted — today{' '}
      <Box
        component="span"
        sx={{ color: progress.today >= COUNT_TARGETS.daily ? stk.green : stk.ink, fontWeight: 700 }}
      >
        {progress.today}/{COUNT_TARGETS.daily}
      </Box>
      {' · '}week{' '}
      <Box
        component="span"
        sx={{ color: progress.week >= COUNT_TARGETS.weekly ? stk.green : stk.ink, fontWeight: 700 }}
      >
        {progress.week}/{COUNT_TARGETS.weekly}
      </Box>
    </Typography>
  ) : (
    <Box
      sx={{
        border: `1px solid ${stk.line}`,
        borderRadius: 2.5,
        px: 1.75,
        py: 1.25,
        bgcolor: stk.field,
      }}
    >
      <Stack spacing={0.75}>
        <Row label="Today" value={progress.today} target={COUNT_TARGETS.daily} />
        <Row label="This week" value={progress.week} target={COUNT_TARGETS.weekly} />
      </Stack>
    </Box>
  );
